import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Paper,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  Grid,
  useTheme,
} from '@mui/material';
import {
  YouTube as YouTubeIcon,
  Description as DescriptionIcon,
  Article as ArticleIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { 
  MainContentArea, 
  MainContentAreaChildGrid
} from './styled';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useNavigate } from 'react-router-dom';

function AddVideo() {

  const theme = useTheme();
  const navigate = useNavigate();

  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [videoDescription, setVideoDescription] = useState('');
  const [transcription, setTranscription] = useState('');
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const [openRecipeDialog, setOpenRecipeDialog] = useState(false);
  const [isUrlValid, setIsUrlValid] = useState(true);
  const [thumbnailUrl, setThumbnailUrl] = useState('');

  // Mock recipes data - replace with your actual data
  const userRecipes = [
    { id: 1, title: 'Chocolate Cake', image: '/cake.jpg' },
    { id: 2, title: 'Pasta Carbonara', image: '/pasta.jpg' },
    { id: 3, title: 'Chicken Curry', image: '/curry.jpg' },
  ];

  const validateYoutubeUrl = (url) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
    return youtubeRegex.test(url);
  };

  const handleYoutubeUrlChange = (event) => {
    const url = event.target.value;
    setYoutubeUrl(url);
    setIsUrlValid(validateYoutubeUrl(url));
    
    // Mock thumbnail generation - in real application, you'd want to fetch this from YouTube API
    if (validateYoutubeUrl(url)) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      if (videoId) {
        setThumbnailUrl(`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`);
      }
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    // Handle form submission
    // console.log({
    //   youtubeUrl,
    //   videoTitle,
    //   videoDescription,
    //   transcription,
    //   selectedRecipes,
    // });
  };

  return (
    <MainContentArea elevation={0}>
      <MainContentAreaChildGrid container >
        <Container 
          sx={{ 
            backgroundColor: `${theme.palette.primary.main}`,
            height: '3rem',
            width: '100%',
            padding: '1rem 1rem',
            borderBottomLeftRadius: '2rem', 
            borderBottomRightRadius: '2rem',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center'
          }}
        >
          <Button
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
              width: '6rem'
            }}
            onClick={() => navigate(-1)}
            color='secondary'
          >
            <ArrowBackOutlinedIcon />
            <Typography variant='h7' color={'secondary'}>
                Back
              </Typography>
          </Button>
        </Container>
        <Container maxWidth="md" sx={{ py: 2 }}>
          <Stack spacing={3}>
            {/* YouTube URL Section */}
            <Paper elevation={3} sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <YouTubeIcon color="error" />
                  YouTube Video Details
                </Typography>
                
                <TextField
                  label="YouTube URL"
                  variant="outlined"
                  fullWidth
                  required
                  value={youtubeUrl}
                  onChange={handleYoutubeUrlChange}
                  error={!isUrlValid && youtubeUrl !== ''}
                  helperText={!isUrlValid && youtubeUrl !== '' ? 'Please enter a valid YouTube URL' : ''}
                />

                {thumbnailUrl && isUrlValid && (
                  <Box sx={{ width: '100%', maxHeight: 200, overflow: 'hidden', borderRadius: 1 }}>
                    <img 
                      src={thumbnailUrl} 
                      alt="Video thumbnail" 
                      style={{ width: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                )}

                <TextField
                  label="Video Title"
                  variant="outlined"
                  fullWidth
                  required
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                />
              </Stack>
            </Paper>

            {/* Description Section */}
            <Paper elevation={3} sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon />
                  Video Description
                </Typography>
                
                <TextField
                  label="Video Description"
                  variant="outlined"
                  fullWidth
                  required
                  multiline
                  rows={4}
                  value={videoDescription}
                  onChange={(e) => setVideoDescription(e.target.value)}
                />
              </Stack>
            </Paper>

            {/* Transcription Section */}
            <Paper elevation={3} sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArticleIcon />
                  Video Transcription
                </Typography>
                
                <TextField
                  label="Video Transcription"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={6}
                  value={transcription}
                  onChange={(e) => setTranscription(e.target.value)}
                  placeholder="Paste your video transcription here..."
                />
              </Stack>
            </Paper>

            {/* Related Recipes Section */}
            <Paper elevation={3} sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Typography variant="h6">Related Recipes</Typography>
                
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={() => setOpenRecipeDialog(true)}
                >
                  Search Your Recipes
                </Button>

                {selectedRecipes.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Recipes:
                    </Typography>
                    <Stack spacing={1}>
                      {selectedRecipes.map((recipe) => (
                        <Chip
                          key={recipe.id}
                          label={recipe.title}
                          onDelete={() => setSelectedRecipes(recipes => 
                            recipes.filter(r => r.id !== recipe.id)
                          )}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Submit Button */}
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={handleSubmit}
              disabled={!isUrlValid || !youtubeUrl || !videoTitle}
            >
              Upload Video Content
            </Button>
          </Stack>

          {/* Recipe Selection Dialog */}
          <Dialog 
            open={openRecipeDialog} 
            onClose={() => setOpenRecipeDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Select Recipes
              <IconButton
                onClick={() => setOpenRecipeDialog(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Search recipes"
                type="search"
                fullWidth
                variant="outlined"
                sx={{ mb: 2 }}
              />
              <List sx={{ pt: 0 }}>
                {userRecipes.map((recipe) => (
                  <ListItem
                    button
                    key={recipe.id}
                    onClick={() => {
                      if (!selectedRecipes.find(r => r.id === recipe.id)) {
                        setSelectedRecipes([...selectedRecipes, recipe]);
                      }
                      setOpenRecipeDialog(false);
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar src={recipe.image} alt={recipe.title}>
                        <ArticleIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={recipe.title} />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
          </Dialog>
        </Container>
      </MainContentAreaChildGrid>
    </MainContentArea>
  )
}

export default AddVideo;